import { notifications } from '@mantine/notifications';

import { ApiError } from '../api/client';
import i18n from '../i18n';

export function notifySaved(message?: string) {
  notifications.show({
    color: 'teal',
    message: message ?? i18n.t('action.save'),
    autoClose: 2500,
  });
}

export function notifyInfo(message: string, title?: string) {
  notifications.show({ color: 'blue', title, message });
}

/**
 * Turns an API failure into something a pilot can act on.
 *
 * DRF field errors are the useful part of a 400, so they are shown rather than a generic
 * "something went wrong" that hides which field the server objected to.
 *
 * The single argument means this can be passed straight to a TanStack mutation's `onError`,
 * which calls it with the error plus the mutation variables.
 */
export function notifyError(error: unknown) {
  const message = (() => {
    if (error instanceof ApiError) {
      if (error.isServerError) return i18n.t('common.serverUnavailable');
      const fieldMessages = Object.values(error.fieldErrors)
        .map((messages) => messages[0])
        .filter(Boolean);
      if (error.detail && !error.detail.startsWith('Request failed')) return error.detail;
      if (fieldMessages.length) return fieldMessages.join(' ');
      return i18n.t('common.error');
    }
    if (error instanceof Error && error.message) return error.message;
    return i18n.t('common.error');
  })();

  notifications.show({ color: 'red', title: i18n.t('common.error'), message, autoClose: 6000 });
}

/** Maps DRF field errors onto a Mantine form so they land next to the offending input. */
export function applyFieldErrors(
  error: unknown,
  setErrors: (errors: Record<string, string>) => void,
) {
  if (!(error instanceof ApiError)) return false;
  const entries = Object.entries(error.fieldErrors).filter(
    ([field]) => field !== 'non_field_errors',
  );
  if (!entries.length) return false;
  setErrors(Object.fromEntries(entries.map(([field, messages]) => [field, messages[0]])));
  return true;
}
